import React, { useRef, useState } from 'react';
import { Camera, Image, X, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * MobilePhotoUpload - Composant optimisé pour l'ajout de photos sur mobile
 * 
 * Props:
 * - onImageSelect: (file: File, preview: string) => void - Callback quand une image est sélectionnée
 * - onImageRemove: () => void - Callback quand l'image est supprimée
 * - currentImage: string | null - URL de prévisualisation de l'image actuelle
 * - label: string - Texte du label (optionnel)
 * - accept: string - Types de fichiers acceptés (défaut: "image/*")
 * - maxSize: number - Taille max en Mo (défaut: 10)
 * - className: string - Classes CSS additionnelles
 * - variant: 'default' | 'compact' | 'full' - Style d'affichage
 * - cameraFirst: boolean - Prioriser la caméra (pour diagnostic photo)
 */
export default function MobilePhotoUpload({
  onImageSelect,
  onImageRemove,
  currentImage = null,
  label = "Ajouter une photo",
  accept = "image/*",
  maxSize = 10,
  className = "",
  variant = "default",
  cameraFirst = false,
  disabled = false
}) {
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setIsLoading(true);

    // Vérifier le type
    if (!file.type.startsWith('image/')) {
      setError("Veuillez sélectionner une image");
      setIsLoading(false);
      return;
    }

    // Vérifier la taille
    if (file.size > maxSize * 1024 * 1024) {
      setError(`L'image ne doit pas dépasser ${maxSize} Mo`);
      setIsLoading(false);
      return;
    }

    // Créer la prévisualisation
    const reader = new FileReader();
    reader.onload = (event) => {
      const preview = event.target?.result;
      onImageSelect(file, preview);
      setIsLoading(false);
    };
    reader.onerror = () => {
      setError("Erreur lors de la lecture de l'image");
      setIsLoading(false);
    };
    reader.readAsDataURL(file);

    // Reset l'input pour permettre de re-sélectionner le même fichier
    e.target.value = '';
  };

  const handleRemove = () => {
    setError(null);
    onImageRemove?.();
  };

  // Rendu compact (pour chat, etc.)
  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {/* Input caméra (hidden) */}
        <input
          ref={cameraInputRef}
          type="file"
          accept={accept}
          capture="environment"
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />
        {/* Input galerie (hidden) */}
        <input
          ref={galleryInputRef}
          type="file"
          accept={accept}
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />

        {currentImage ? (
          <div className="relative">
            <img 
              src={currentImage} 
              alt="Aperçu" 
              className="w-10 h-10 rounded-lg object-cover border border-zinc-700"
            />
            <button
              onClick={handleRemove}
              className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center"
              disabled={disabled}
            >
              <X className="w-3 h-3 text-white" />
            </button>
          </div>
        ) : (
          <>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => cameraInputRef.current?.click()}
              disabled={disabled || isLoading}
              className="text-zinc-400 hover:text-gold-400"
              title="Prendre une photo"
              data-testid="mobile-photo-camera-btn-compact"
            >
              <Camera className="w-5 h-5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => galleryInputRef.current?.click()}
              disabled={disabled || isLoading}
              className="text-zinc-400 hover:text-gold-400"
              title="Choisir dans la galerie"
              data-testid="mobile-photo-gallery-btn-compact"
            >
              <Image className="w-5 h-5" />
            </Button>
          </>
        )}
      </div>
    );
  }

  // Rendu par défaut ou full
  return (
    <div className={`space-y-3 ${className}`}>
      {/* Input caméra (hidden) */}
      <input
        ref={cameraInputRef}
        type="file"
        accept={accept}
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />
      {/* Input galerie (hidden) */}
      <input
        ref={galleryInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />

      <AnimatePresence mode="wait">
        {currentImage ? (
          // Prévisualisation de l'image
          <motion.div
            key="preview"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative rounded-xl overflow-hidden border border-zinc-700 bg-zinc-900"
          >
            <img 
              src={currentImage} 
              alt="Aperçu" 
              className={`w-full object-cover ${variant === 'full' ? 'max-h-96' : 'max-h-48'}`}
            />
            
            {/* Overlay avec actions */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex items-end justify-center p-4 gap-3">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => cameraInputRef.current?.click()}
                disabled={disabled || isLoading}
                className="bg-zinc-800/90 hover:bg-zinc-700 text-white"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Changer
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={handleRemove}
                disabled={disabled}
              >
                <X className="w-4 h-4 mr-2" />
                Supprimer
              </Button>
            </div>
          </motion.div>
        ) : (
          // Boutons d'ajout
          <motion.div
            key="buttons"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`
              ${variant === 'full' 
                ? 'p-8 border-2 border-dashed border-zinc-700 rounded-xl bg-zinc-900/50' 
                : 'p-4 border border-zinc-800 rounded-xl bg-zinc-900/30'
              }
            `}
          >
            {label && (
              <p className="text-center text-zinc-400 text-sm mb-4">{label}</p>
            )}
            
            <div className={`
              flex gap-3 
              ${variant === 'full' ? 'flex-col sm:flex-row justify-center' : 'justify-center'}
            `}>
              {/* Bouton Caméra - Prioritaire si cameraFirst */}
              <Button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                disabled={disabled || isLoading}
                className={`
                  flex items-center gap-2 
                  ${cameraFirst 
                    ? 'bg-gold-500 hover:bg-gold-400 text-black font-bold' 
                    : 'bg-zinc-800 hover:bg-zinc-700 text-white'
                  }
                  ${variant === 'full' ? 'px-6 py-3' : 'px-4 py-2'}
                `}
                data-testid="mobile-photo-camera-btn"
              >
                <Camera className="w-5 h-5" />
                <span className="whitespace-nowrap">
                  {isLoading ? 'Chargement...' : 'Prendre une photo'}
                </span>
              </Button>

              {/* Bouton Galerie */}
              <Button
                type="button"
                variant={cameraFirst ? "outline" : "secondary"}
                onClick={() => galleryInputRef.current?.click()}
                disabled={disabled || isLoading}
                className={`
                  flex items-center gap-2
                  ${variant === 'full' ? 'px-6 py-3' : 'px-4 py-2'}
                  ${cameraFirst ? 'border-zinc-700' : ''}
                `}
                data-testid="mobile-photo-gallery-btn"
              >
                <Image className="w-5 h-5" />
                <span className="whitespace-nowrap">Galerie</span>
              </Button>
            </div>

            {/* Indication */}
            <p className="text-center text-zinc-600 text-xs mt-3">
              {accept === "image/*,application/pdf" 
                ? "Photo ou PDF accepté" 
                : "JPG, PNG, HEIC acceptés"
              } • Max {maxSize} Mo
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Message d'erreur */}
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-red-400 text-sm text-center"
        >
          {error}
        </motion.p>
      )}
    </div>
  );
}
